/**
 * CortisolReportCard — отчет „преди и сега“ за 28-дневната програма.
 * Симптоми в началото и сега, кривата по точки, изпълнение по седмици,
 * сън и дишане. Данни: GET /api/cortisol/report
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  FileBarChart, ArrowDown, ArrowUp, Minus, ChevronDown, Loader2,
} from "lucide-react";
import { useAuth } from "@/App";
import SaveToHealthIntelligenceButton from "@/components/SaveToHealthIntelligenceButton";

const VERDICT = {
  better: "bg-emerald-500/15 text-emerald-600",
  same: "bg-amber-500/15 text-amber-600",
  worse: "bg-orange-500/20 text-orange-600",
  no_data: "bg-slate-500/15",
};
const DIR = {
  down: { Icon: ArrowDown, color: "#10B981" },
  up: { Icon: ArrowUp, color: "#F59E0B" },
  same: { Icon: Minus, color: "#94A3B8" },
};

export default function CortisolReportCard({ isDark = false, refreshKey = 0 }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cortisol/report`);
      setD(data);
    } catch { setD(null); } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d?.available) return null;

  const border = isDark ? "border-white/10" : "border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 mt-3 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
      data-testid="cortisol-report-card">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="cortisol-report-toggle">
        <span className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
          <FileBarChart className="w-4 h-4 text-violet-500" />
          Отчет „преди и сега“
          {d.interim && <span className="text-[10px] font-normal opacity-60">
            · междинен, ден {d.day} от 28</span>}
        </span>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" />
          : <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${VERDICT[d.verdict]}`}
        data-testid="cortisol-report-verdict">
        {d.symptoms.before.high + d.symptoms.before.low} → {d.symptoms.now.high + d.symptoms.now.low} оплаквания
      </div>
      {d.rounds_note_bg && (
        <div className="text-[10px] opacity-70 mt-1" data-testid="cortisol-report-rounds">
          Изпълнение по кръгове: {d.rounds_note_bg}
        </div>
      )}
      <div className="text-[11px] opacity-80 mt-1">{d.verdict_bg}</div>

      {open && (
        <div className="mt-3 space-y-3 text-[11px]" data-testid="cortisol-report-body">
          {/* симптоми */}
          <div className={`p-2.5 rounded-xl border ${border}`} data-testid="cortisol-report-symptoms">
            <div className="text-[10px] font-bold uppercase opacity-60">Симптоми</div>
            <div className="mt-1">
              В началото: висок {d.symptoms.before.high} · нисък {d.symptoms.before.low}
              {" → "}сега: висок {d.symptoms.now.high} · нисък {d.symptoms.now.low}
            </div>
            {!!d.symptoms.resolved_bg?.length && (
              <div className="mt-1 text-emerald-600" data-testid="cortisol-report-resolved">
                Отпаднаха: {d.symptoms.resolved_bg.join(" · ")}
              </div>
            )}
            {!!d.symptoms.appeared_bg?.length && (
              <div className="mt-1 text-amber-600" data-testid="cortisol-report-appeared">
                Нови: {d.symptoms.appeared_bg.join(" · ")}
              </div>
            )}
          </div>

          {/* крива */}
          <div className={`p-2.5 rounded-xl border ${border}`} data-testid="cortisol-report-curve">
            <div className="text-[10px] font-bold uppercase opacity-60">Кривата на кортизола</div>
            {d.curve?.length ? (
              <div className="mt-1 space-y-1">
                {d.curve.map((r) => {
                  const dir = DIR[r.direction] || DIR.same;
                  return (
                    <div key={r.key} className="flex items-center justify-between gap-2"
                      data-testid={`cortisol-report-point-${r.key}`}>
                      <span className="opacity-80">{r.label_bg}</span>
                      <span className="inline-flex items-center gap-1 font-bold">
                        {r.before ?? "—"} → {r.now ?? "—"}
                        {r.delta !== null && r.delta !== undefined && (
                          <dir.Icon className="w-3.5 h-3.5" style={{ color: dir.color }} />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="opacity-70 mt-1">{d.curve_note_bg}</div>
            )}
          </div>

          {/* изпълнение */}
          <div className={`p-2.5 rounded-xl border ${border}`} data-testid="cortisol-report-weeks">
            <div className="text-[10px] font-bold uppercase opacity-60">Изпълнение по седмици</div>
            <div className="mt-1 space-y-1">
              {d.weeks.map((w) => (
                <div key={w.n} data-testid={`cortisol-report-week-${w.n}`}>
                  <div className="flex items-center justify-between">
                    <span>{w.n}. {w.title_bg}</span><span className="font-bold">{w.percent}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden bg-slate-500/20">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                      style={{ width: `${w.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1.5 opacity-70" data-testid="cortisol-report-extras">
              Серия: {d.streak} дни · дишане: {d.breath.sessions} сесии ({d.breath.minutes} мин)
              {d.sleep.avg_quality ? ` · сън: ${d.sleep.avg_quality}/5 от ${d.sleep.nights} нощи` : ""}
            </div>
          </div>

          <div className="text-[9px] opacity-50">{d.note_bg}</div>

          <SaveToHealthIntelligenceButton
            snapshotType="digital_twin"
            title={`Кортизолов отчет — ден ${d.day} от 28`}
            summary={`Оплаквания ${d.symptoms.before.high + d.symptoms.before.low} → `
              + `${d.symptoms.now.high + d.symptoms.now.low} · изпълнение `
              + `${d.progress.percent}% · серия ${d.streak} дни`}
            payload={{ source: "cortisol_intelligence", module: "cortisol", report: d }}
            tags={["cortisol", "stress", d.verdict].filter(Boolean)}
            testId="cortisol-report-save-hi"
          />
        </div>
      )}
    </div>
  );
}
